import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "@phosphor-icons/react";
import { Reveal } from "../Reveal";

const EVOLUTION = [
    "Medical Tourism",
    "Healthcare Navigation",
    "AI-Powered Patient Journey",
    "International Patient OS",
    "Cross-Border Healthcare Infrastructure",
];

export default function Vision() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

    return (
        <section ref={ref} data-testid="vision-section" className="relative py-32 lg:py-44 bg-tint-blue noise-overlay overflow-hidden">
            <motion.div style={{ y }} className="absolute -top-24 right-0 font-heading text-[16rem] leading-none font-bold text-brand-blue/[0.04] select-none pointer-events-none hidden lg:block" aria-hidden="true">
                Vision
            </motion.div>
            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <Reveal>
                    <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-green">
                        <span className="text-slate-400">11&nbsp;&nbsp;·&nbsp;&nbsp;</span>Our Vision
                    </p>
                </Reveal>
                <Reveal delay={0.1}>
                    <h2 data-testid="vision-statement" className="mt-6 max-w-5xl font-heading text-3xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.08] text-brand-blue">
                        We are not building another medical-tourism agency.
                        <span className="block mt-3 text-brand-green">We are building the infrastructure for cross-border healthcare.</span>
                    </h2>
                </Reveal>

                <div className="mt-20 max-w-2xl">
                    {EVOLUTION.map((step, i) => (
                        <Reveal key={step} delay={0.08 * i}>
                            <div data-testid={`vision-step-${i + 1}`} className="flex flex-col">
                                <div
                                    className={`flex items-center gap-5 rounded-2xl border px-7 py-5 transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 ${
                                        i === EVOLUTION.length - 1
                                            ? "bg-brand-blue border-brand-blue text-white shadow-xl shadow-brand-blue/25"
                                            : "bg-white/80 backdrop-blur border-white text-brand-blue shadow-sm"
                                    }`}
                                    style={{ marginLeft: `${i * 1.5}rem` }}
                                >
                                    <span className={`font-heading text-sm font-bold ${i === EVOLUTION.length - 1 ? "text-brand-green-light" : "text-brand-green"}`}>
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="font-heading text-lg sm:text-xl font-semibold tracking-tight">{step}</span>
                                </div>
                                {i < EVOLUTION.length - 1 && (
                                    <ArrowDown size={16} className="my-2 text-brand-green/60" style={{ marginLeft: `${i * 1.5 + 1.75}rem` }} />
                                )}
                            </div>
                        </Reveal>
                    ))}
                </div>

                <Reveal delay={0.3}>
                    <p data-testid="vision-supporting" className="mt-16 max-w-2xl text-base md:text-lg leading-relaxed text-slate-600">
                        Our long-term vision is to create an intelligent healthcare infrastructure connecting patients,
                        healthcare providers, technology and trusted partners across borders.
                    </p>
                </Reveal>
            </div>
        </section>
    );
}
